import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import * as moment from 'moment/moment';
import { AuthService } from '@services';
import { LocalStorageService } from 'ngx-webstorage';
import { interval, Subscription } from 'rxjs';
import { Banner, Banners } from './banners';

@Component({
  selector: 'app-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BannerComponent implements OnInit, OnDestroy {
  bannerShown = true;
  bannerSubscription: Subscription;
  banners: Banner[];
  currentBanner: {
    item: Banner;
    index: number;
  };
  bannerInterval = 15000;
  @Output() closeBanner: EventEmitter<void> = new EventEmitter<void>();

  get user() {
    return this.authService.user;
  }

  constructor(
    protected storage: LocalStorageService,
    protected authService: AuthService,
    protected cd: ChangeDetectorRef,
  ) {
  }

  bannerClose(): void {
    this.bannerShown = false;
    this.storage.store('banner_shown', this.bannerShown);
    this.bannerSubscription?.unsubscribe();
    this.closeBanner.next();
  }

  prepareBanners(): void {
    this.banners = Banners
      .filter(banner => !banner.disabled)
      .filter(banner => banner.visibleFor.some(i => i === this.user.loyaltyCardType) && this.checkBannerDates(banner));
  }

  changeBanner(banner: { item, index }, action: 'next' | 'prev', resetTimer: boolean = true): void {
    const newIndex = action === 'next' ? banner.index + 1 : banner.index - 1;
    if (this.banners[newIndex]) {
      this.currentBanner = {
        item: this.banners[newIndex],
        index: newIndex,
      };
    } else {
      this.currentBanner = action === 'next' ?
        {
          item: this.banners[0],
          index: 0,
        } :
        {
          item: this.banners[this.banners.length - 1],
          index: this.banners.length - 1,
        };
    }
    if (resetTimer) {
      this.initBanner();
    }
    this.cd.detectChanges();
  }

  initBanner(): void {
    this.bannerSubscription?.unsubscribe();
    this.bannerSubscription = interval(this.bannerInterval)
      .subscribe(() => this.changeBanner(this.currentBanner, 'next', false));
  }

  ngOnInit(): void {
    this.prepareBanners();
    this.currentBanner = {
      item: this.banners[0],
      index: 0,
    };
    const item = localStorage.getItem('ng2-webstorage|banner_shown');
    this.bannerShown = item === null
      ? true
      : item === 'true';
    if (this.bannerShown) {
      this.initBanner();
    } else {
      this.closeBanner.next();
    }
    this.cd.detectChanges();
  }

  ngOnDestroy(): void {
    this.bannerSubscription?.unsubscribe();
  }

  private checkBannerDates(banner: Banner): boolean {
    const format = 'DD-MM-YYYY';
    const today = moment();

    const dateFrom = banner.dateFrom ? moment(banner.dateFrom, format) : null;
    const dateTo = banner.dateTo ? moment(banner.dateTo, format) : null;

    if (!dateFrom && !dateTo) {
      return true;
    }

    if (dateFrom && !dateTo) {
      return today.isSameOrAfter(dateFrom, 'day');
    }

    if (!dateFrom && dateTo) {
      return today.isSameOrBefore(dateTo, 'day');
    }

    return today.isBetween(dateFrom, dateTo, 'days', '[]');
  }
}
